// 1. React and core hooks
import { useCallback, useEffect, useRef, useState } from 'react';

// 2. External libraries
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from '@tanstack/react-router';

// 3. Type imports
import type { AnyWebSocketEvent } from '~/types/websocket';

// 4. Custom hooks and utilities
import { getWsBaseUrl } from '~/lib/api';
import { logDebug } from '~/utils/logger';

// Auth error codes that should not trigger reconnection (moved outside hook to avoid dependency issues)
const AUTH_ERROR_CODES = [4001, 4002, 4003]; // Invalid token, expired, limit exceeded

// Routes where WebSocket should not connect (user may not exist in DB yet, or connection limit issues)
const WS_EXEMPT_ROUTES = [
  '/sign-in',
  '/sso-callback',
  '/magic-link-callback',
  '/telegram/connect',
  '/oauth/authorize',
];

export interface UseWebSocketOptions {
  /** Callback invoked when a message is received (excluding pong responses) */
  onMessage?: (event: AnyWebSocketEvent) => void;
  /** Callback invoked when WebSocket connection is established */
  onConnect?: () => void;
  /** Callback invoked when WebSocket connection is closed */
  onDisconnect?: () => void;
  /** Maximum reconnection attempts before giving up (default: 10) */
  reconnectAttempts?: number;
  /** Interval in ms between heartbeat pings (default: 30000) */
  heartbeatInterval?: number;
}

export interface UseWebSocketReturn {
  /** Whether WebSocket is currently connected */
  isConnected: boolean;
  /** Whether reconnection attempts have been exhausted */
  isReconnectExhausted: boolean;
  /** Manually disconnect WebSocket and stop reconnection attempts */
  disconnect: () => void;
  /** Manually reconnect WebSocket (resets retry counter) */
  reconnect: () => Promise<void>;
}

/**
 * Custom hook for managing WebSocket connection to DeadDrop manage endpoint.
 *
 * Handles authentication via Clerk JWT, automatic reconnection with exponential
 * backoff, and heartbeat pings to keep connection alive. Automatically connects
 * on mount and cleans up on unmount.
 *
 * Per websocket-notifications-spec.md, events use generic wrapper structure:
 * { type: 'event.type', detail?: 'context', data: ResponseModel }
 *
 * @param options - Configuration options for WebSocket behavior
 * @returns Object containing connection state and control functions
 *
 * @example
 * ```tsx
 * function NotificationListener() {
 *   const queryClient = useQueryClient();
 *
 *   const { isConnected } = useWebSocket({
 *     onMessage: (wsEvent) => {
 *       if (wsEvent.type === 'changed' && wsEvent.data.entity === 'messages') {
 *         queryClient.invalidateQueries({ queryKey: ['topic-messages', wsEvent.data.topicId] });
 *       }
 *     },
 *     onConnect: () => console.log('WebSocket connected'),
 *     onDisconnect: () => console.log('WebSocket disconnected'),
 *   });
 *
 *   return <ConnectionIndicator connected={isConnected} />;
 * }
 * ```
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    reconnectAttempts = 10,
    heartbeatInterval = 30000,
  } = options;

  const { getToken } = useAuth();
  const location = useLocation();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const connectRef = useRef<(() => Promise<void>) | null>(null);
  const manualDisconnectRef = useRef(false);
  const isConnectingRef = useRef<boolean>(false);

  // Store callbacks in refs to avoid reconnection on callback changes
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
  }, [onMessage, onConnect, onDisconnect]);

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnectExhausted, setIsReconnectExhausted] = useState(false);

  // Check if we're on a route where WebSocket should not connect
  const isExemptRoute = WS_EXEMPT_ROUTES.includes(location.pathname);

  const connect = useCallback(async () => {
    // Guard against duplicate connections: an OPEN socket or an in-flight
    // connection attempt. Run before await getToken() so concurrent calls
    // cannot race past the token fetch and spawn parallel sockets.
    if (wsRef.current?.readyState === WebSocket.OPEN || isConnectingRef.current === true) return;

    // Don't connect on exempt routes (user may not exist in DB yet)
    if (isExemptRoute) return;

    // Claim the in-flight slot before awaiting getToken() so a concurrent
    // connect() cannot race past the token fetch and spawn a parallel socket.
    // Every exit path below must clear this flag; onopen/onclose clear it once
    // the socket is constructed.
    isConnectingRef.current = true;

    let token: string | null;
    try {
      token = await getToken();
    } catch (error) {
      // A failed token fetch must not permanently wedge the guard.
      isConnectingRef.current = false;
      throw error;
    }
    if (!token) {
      isConnectingRef.current = false;
      return;
    }

    manualDisconnectRef.current = false;
    const wsUrl = `${getWsBaseUrl()}/ws/manage`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    // Heartbeat interval bound to this specific socket instance via the
    // closure. This socket's own onclose clears its captured interval.
    let socketHeartbeat: ReturnType<typeof setInterval> | undefined;

    ws.onopen = () => {
      // Authenticate over the socket as the first frame. The token must travel
      // in the message body, not the URL query string, so it never lands in
      // server access logs or proxy history.
      ws.send(JSON.stringify({ type: 'auth', token }));

      isConnectingRef.current = false;
      setIsConnected(true);
      setIsReconnectExhausted(false);
      reconnectCountRef.current = 0;
      onConnectRef.current?.();

      // Start heartbeat
      socketHeartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, heartbeatInterval);
    };

    ws.onmessage = (event) => {
      // Isolate malformed frames. A non-JSON payload must not throw
      // out of the socket callback or tear down the connection. Log and ignore.
      let wsEvent: AnyWebSocketEvent | { type: 'pong' };
      try {
        const raw: string = typeof event.data === 'string' ? event.data : String(event.data);
        wsEvent = JSON.parse(raw) as AnyWebSocketEvent | { type: 'pong' };
      } catch (error) {
        console.warn('Discarding malformed WebSocket message', error);
        return;
      }
      // Handle pong responses separately (not part of AnyWebSocketEvent union)
      if (wsEvent.type !== 'pong') {
        onMessageRef.current?.(wsEvent);
      }
    };

    ws.onerror = () => ws.close();

    ws.onclose = (event) => {
      isConnectingRef.current = false;

      // Always clear this socket's own captured heartbeat.
      if (socketHeartbeat) {
        clearInterval(socketHeartbeat);
        socketHeartbeat = undefined;
      }

      // Identity check: if a newer socket has superseded this one, this is a
      // stale close. Don't touch shared connection state or schedule reconnect.
      if (wsRef.current !== ws) {
        return;
      }

      setIsConnected(false);
      onDisconnectRef.current?.();

      // Don't reconnect if manually disconnected
      if (manualDisconnectRef.current) {
        return;
      }

      // Don't reconnect on auth errors (invalid/expired token, limit exceeded)
      if (AUTH_ERROR_CODES.includes(event.code)) {
        console.warn(`WebSocket auth error (code ${event.code}): ${event.reason}`);
        setIsReconnectExhausted(true);
        return;
      }

      // Exponential backoff reconnection
      if (reconnectCountRef.current < reconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectCountRef.current), 30000);
        reconnectCountRef.current++;
        logDebug(
          `WebSocket reconnecting in ${delay}ms (attempt ${reconnectCountRef.current}/${reconnectAttempts})`
        );
        reconnectTimeoutRef.current = setTimeout(() => {
          void connectRef.current?.();
        }, delay);
      } else {
        console.warn(`WebSocket reconnection attempts exhausted (${reconnectAttempts})`);
        setIsReconnectExhausted(true);
      }
    };
  }, [getToken, reconnectAttempts, heartbeatInterval, isExemptRoute]);

  // Keep connectRef in sync with connect
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    isConnectingRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    // Close the socket but leave wsRef.current pointing at it. The socket's own
    // onclose then passes its identity check (wsRef.current === ws), so it
    // clears its captured heartbeat and runs setIsConnected(false); the
    // manualDisconnectRef guard there suppresses reconnect. Nulling wsRef here
    // would make that close look stale and leave isConnected stuck true. The
    // next connect() reassigns wsRef, so a later duplicate onclose still reads
    // as stale.
    wsRef.current?.close();
  }, []);

  const reconnect = useCallback(async () => {
    // Reset retry state for manual reconnect
    reconnectCountRef.current = 0;
    setIsReconnectExhausted(false);
    await connect();
  }, [connect]);

  // Connect when on non-exempt route, disconnect when navigating to exempt route
  useEffect(() => {
    if (isExemptRoute) {
      disconnect();
    } else {
      void connect();
    }
    return () => disconnect();
  }, [connect, disconnect, isExemptRoute]);

  return { isConnected, isReconnectExhausted, disconnect, reconnect };
}
